import { IconCheck, IconPhoto } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import type { BulkListingSession } from 'app/network/listing';
import { createTheme } from 'app/shopify';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';

const theme = createTheme();

export function BulkCompletionStep({ onClose, session }: { onClose: () => void; session?: BulkListingSession }) {
  const { t } = useTranslation('new');
  const published = session?.progress.saved ?? 0;
  const skipped = session?.progress.skipped ?? 0;
  const publishedPhotos = session?.drafts.filter((draft) => draft.status === 'saved').slice(0, 3) ?? [];

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.halo}>
          <View style={styles.icon}>
            {published > 0 ? (
              <IconCheck size={40} color={theme.colors['surface.default']} />
            ) : (
              <IconPhoto size={36} color={theme.colors['surface.default']} />
            )}
          </View>
        </View>
        <Column alignItems="center" gap="spacing.general.x3">
          <Text color="text.primary" variant="headerXSmall" fontWeight="700" textAlign="center">
            {t(published > 0 ? 'bulk.completion.title' : 'bulk.completion.emptyTitle')}
          </Text>
          <Text color="text.secondary" variant="bodyBase" textAlign="center">
            {t('bulk.completion.published', { count: published })}
          </Text>
          {skipped > 0 ? (
            <Text color="text.secondary" variant="bodySmall" textAlign="center">
              {t('bulk.completion.skipped', { count: skipped })}
            </Text>
          ) : null}
        </Column>
        {publishedPhotos.length ? (
          <Row gap="spacing.general.x2" accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
            {publishedPhotos.map((draft) => (
              <PCImage key={draft.id} source={{ uri: draft.imageUrl }} contentFit="cover" style={styles.thumbnail} />
            ))}
          </Row>
        ) : null}
      </ScrollView>
      <View style={styles.footer}>
        <Button onPress={onClose} variant="primary" style={styles.done}>
          {t('bulk.completion.close')}
        </Button>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 24, gap: 24 },
  halo: { padding: 12, borderRadius: 60, backgroundColor: theme.colors['surface.brand.subtle'] },
  icon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: theme.colors['surface.brand.default'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnail: {
    width: 72,
    height: 88,
    borderRadius: theme.borderRadii['radius.lg'],
    backgroundColor: theme.colors['surface.tertiary-neutral'],
  },
  footer: { paddingHorizontal: 24, paddingTop: 16, paddingBottom: 8 },
  done: {
    minHeight: 56,
    borderRadius: theme.borderRadii['radius.lg'],
    backgroundColor: theme.colors['surface.brand.default'],
  },
});
